/**
 * WOClockInOutTab - Live clock-in / clock-out for work order field staff.
 * Allows technicians to track real time spent on site.
 */
import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell,
  TableHead, TableRow, Chip, Alert, CircularProgress, Divider,
} from "@mui/material";
import AccessTimeIcon    from "@mui/icons-material/AccessTime";
import LoginIcon         from "@mui/icons-material/Login";
import LogoutIcon        from "@mui/icons-material/Logout";
import TimerIcon         from "@mui/icons-material/Timer";
import api from "../../../utils/api";

const cardSx = {
  borderRadius:"14px", border:"1px solid rgba(47,99,173,0.08)",
  boxShadow:"0 4px 16px rgba(0,0,0,0.05)", p:2.5, mb:2.5,
};

const fmtDT  = (d) => d ? new Date(d).toLocaleString() : "N/A";
const fmtDur = (sec) => {
  if (!sec && sec !== 0) return "N/A";
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  return `${h}h ${m}m ${s}s`;
};

// Live elapsed seconds counter
const LiveTimer = ({ startTime }) => {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const base = startTime ? Math.floor((Date.now() - new Date(startTime).getTime()) / 1000) : 0;
    setElapsed(base);
    const t = setInterval(() => setElapsed(p => p + 1), 1000);
    return () => clearInterval(t);
  }, [startTime]);
  return <span>{fmtDur(elapsed)}</span>;
};

const WOClockInOutTab = ({ workOrderId, currentUserId }) => {
  const [sessions,    setSessions]    = useState([]);
  const [active,      setActive]      = useState(null);   // active session if any
  const [loading,     setLoading]     = useState(true);
  const [acting,      setActing]      = useState(false);
  const [error,       setError]       = useState("");
  const [snack,       setSnack]       = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sessRes, activeRes] = await Promise.allSettled([
        api.get(`/workorder-time-logs/${workOrderId}`),
        api.get(`/work-orders/${workOrderId}/active-session`),
      ]);
      if (sessRes.status === "fulfilled") setSessions(sessRes.value.data || []);
      if (activeRes.status === "fulfilled") setActive(activeRes.value.data?.session || null);
    } catch {}
    finally { setLoading(false); }
  }, [workOrderId]);

  useEffect(() => { load(); }, [load]);

  const handleClockIn = async () => {
    setActing(true); setError("");
    try {
      await api.post(`/work-orders/${workOrderId}/clock-in`, { user_id: currentUserId });
      setSnack("Clocked in successfully.");
      load();
    } catch (e) {
      setError(e.response?.data?.error || "Clock-in failed.");
    } finally { setActing(false); }
  };

  const handleClockOut = async () => {
    setActing(true); setError("");
    try {
      await api.post(`/work-orders/${workOrderId}/clock-out`, { user_id: currentUserId });
      setSnack("Clocked out successfully.");
      load();
    } catch (e) {
      setError(e.response?.data?.error || "Clock-out failed.");
    } finally { setActing(false); }
  };

  // summary
  const totalMin = sessions.reduce((acc, s) => acc + (s.duration_minutes || 0), 0);
  const billableMin = sessions.filter(s => s.is_billable).reduce((acc, s) => acc + (s.duration_minutes || 0), 0);

  return (
    <Box>
      {snack && <Alert severity="success" onClose={() => setSnack("")} sx={{ mb:2, borderRadius:2 }}>{snack}</Alert>}
      {error  && <Alert severity="error"   onClose={() => setError("")} sx={{ mb:2, borderRadius:2 }}>{error}</Alert>}

      {/* Clock In/Out Card */}
      <Paper elevation={0} sx={{ ...cardSx, border:"2px solid rgba(47,99,173,0.15)" }}>
        <Box sx={{ display:"flex", alignItems:"center", gap:1.5, mb:2 }}>
          <TimerIcon sx={{ color:"#2f63ad", fontSize:28 }} />
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight:700, color:"#223142" }}>
              {active ? "Currently On Site" : "Clock In to Start Tracking"}
            </Typography>
            {active && (
              <Typography variant="caption" sx={{ color:"#6b778c" }}>
                Clocked in at {fmtDT(active.ClockIn)} &mdash; elapsed: <LiveTimer startTime={active.ClockIn} />
              </Typography>
            )}
          </Box>
          <Box sx={{ ml:"auto", display:"flex", gap:1 }}>
            {!active ? (
              <Button variant="contained" startIcon={acting ? <CircularProgress size={16} color="inherit" /> : <LoginIcon />}
                onClick={handleClockIn} disabled={acting}
                sx={{ borderRadius:"10px", fontWeight:700, textTransform:"none", boxShadow:"none",
                  bgcolor:"#16a34a", "&:hover":{ bgcolor:"#15803d", boxShadow:"none" } }}>
                Clock In
              </Button>
            ) : (
              <Button variant="contained" startIcon={acting ? <CircularProgress size={16} color="inherit" /> : <LogoutIcon />}
                onClick={handleClockOut} disabled={acting}
                sx={{ borderRadius:"10px", fontWeight:700, textTransform:"none", boxShadow:"none",
                  bgcolor:"#dc2626", "&:hover":{ bgcolor:"#b91c1c", boxShadow:"none" } }}>
                Clock Out
              </Button>
            )}
          </Box>
        </Box>

        {/* Summary */}
        <Box sx={{ display:"flex", gap:3, flexWrap:"wrap" }}>
          {[
            { label:"Total Sessions", value: sessions.length, color:"#223142" },
            { label:"Total Time",     value: `${Math.floor(totalMin/60)}h ${totalMin%60}m`, color:"#2f63ad" },
            { label:"Billable Time",  value: `${Math.floor(billableMin/60)}h ${billableMin%60}m`, color:"#16a34a" },
          ].map(({ label, value, color }) => (
            <Box key={label} sx={{ textAlign:"center", minWidth:90 }}>
              <Typography variant="h5" sx={{ fontWeight:800, color }}>{value}</Typography>
              <Typography variant="caption" sx={{ color:"#6b778c" }}>{label}</Typography>
            </Box>
          ))}
        </Box>
      </Paper>

      {/* Session Log */}
      <Paper elevation={0} sx={{ borderRadius:"12px", border:"1px solid rgba(47,99,173,0.08)", overflow:"hidden" }}>
        <Box sx={{ px:2, py:1.5, bgcolor:"rgba(47,99,173,0.03)", borderBottom:"1px solid rgba(47,99,173,0.08)" }}>
          <Typography variant="subtitle2" sx={{ fontWeight:700, color:"#223142" }}>Session Log</Typography>
        </Box>
        {loading ? (
          <Box sx={{ display:"flex", justifyContent:"center", py:4 }}><CircularProgress size={24} /></Box>
        ) : (
          <Table size="small">
            <TableHead sx={{ bgcolor:"rgba(47,99,173,0.03)" }}>
              <TableRow>
                {["Technician","Clock In","Clock Out","Duration","Billable","Notes"].map(h => (
                  <TableCell key={h} sx={{ fontWeight:700, color:"#223142", fontSize:12 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sessions.map((s, i) => (
                <TableRow key={s.id || i} hover>
                  <TableCell sx={{ fontWeight:600, color:"#223142", fontSize:12 }}>
                    {s.user_name || s.technician_name || "Staff"}
                  </TableCell>
                  <TableCell sx={{ fontSize:12, color:"#6b778c" }}>{fmtDT(s.start_time || s.ClockIn)}</TableCell>
                  <TableCell sx={{ fontSize:12, color:"#6b778c" }}>{fmtDT(s.end_time   || s.ClockOut) || <Chip size="small" label="Active" sx={{ bgcolor:"rgba(22,163,74,0.1)", color:"#16a34a", fontWeight:700, fontSize:10 }} />}</TableCell>
                  <TableCell sx={{ fontSize:12, color:"#374151", fontWeight:600 }}>
                    {s.duration_minutes != null ? `${Math.floor(s.duration_minutes/60)}h ${s.duration_minutes%60}m` : "In Progress"}
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={s.is_billable ? "Yes" : "No"}
                      sx={{ fontWeight:600, fontSize:10, borderRadius:"6px",
                        bgcolor: s.is_billable ? "rgba(22,163,74,0.1)" : "rgba(0,0,0,0.06)",
                        color:   s.is_billable ? "#16a34a" : "#6b778c" }} />
                  </TableCell>
                  <TableCell sx={{ fontSize:11, color:"#6b778c", maxWidth:160 }}>{s.notes || "-"}</TableCell>
                </TableRow>
              ))}
              {!sessions.length && (
                <TableRow>
                  <TableCell colSpan={6} sx={{ textAlign:"center", py:4, color:"#6b778c" }}>
                    No sessions recorded yet. Click "Clock In" when you arrive on site.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </Paper>
    </Box>
  );
};

export default WOClockInOutTab;
